import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api } from '../api/client.js';
import type { ModelSummary } from '@wml-crm/shared';

interface AccountLike {
  id: string;
  slaveName: string;
  handle: string | null;
}

interface Props {
  account: AccountLike;
  currentModelName: string;
  currentModelId: string;
  /** Каким API-endpoint'ом двинуть акк. По умолчанию — публичный /api/accounts/:id/move. */
  endpoint?: 'public' | 'admin';
  onClose: () => void;
  onDone: (newModelId: string) => void;
}

export function MoveAccountModal({
  account, currentModelName, currentModelId, endpoint = 'public', onClose, onDone,
}: Props) {
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [q, setQ] = useState('');
  const [targetModelId, setTargetModelId] = useState<string | null>(null);
  const [newModelName, setNewModelName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<ModelSummary[]>('/api/models').then(setModels).catch(() => setModels([]));
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const filtered = useMemo(() => {
    const others = models.filter((m) => m.id !== currentModelId);
    if (!q) return others;
    const needle = q.toLowerCase();
    return others.filter((m) => m.displayName.toLowerCase().includes(needle));
  }, [models, q, currentModelId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = { reason };
      if (mode === 'existing') {
        if (!targetModelId) throw new Error('Выбери модель или создай новую');
        body.targetModelId = targetModelId;
      } else {
        if (!newModelName.trim()) throw new Error('Введи название новой модели');
        body.targetModelName = newModelName.trim();
      }
      const path = endpoint === 'admin'
        ? '/api/admin/move-account'
        : `/api/accounts/${account.id}/move`;
      if (endpoint === 'admin') body.accountId = account.id;
      const r = await api<{ modelId: string }>(path, { method: 'POST', body: JSON.stringify(body) });
      onDone(r.modelId);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer" role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>Перенести аккаунт</div>
            <div style={{ color: 'var(--text-subtle)', fontSize: 12 }}>
              {account.slaveName} · сейчас у «{currentModelName}»
            </div>
          </div>
          <button className="icon-btn" title="Закрыть (Esc)" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className="drawer-body" onSubmit={submit}>
          <div className="range-tabs" style={{ alignSelf: 'flex-start' }}>
            <button type="button" className={mode === 'existing' ? 'active' : ''} onClick={() => setMode('existing')}>Существующая</button>
            <button type="button" className={mode === 'new' ? 'active' : ''} onClick={() => setMode('new')}>Новая модель</button>
          </div>

          {mode === 'existing' ? (
            <>
              <div className="field">
                <label>Фильтр моделей</label>
                <input
                  className="admin-input"
                  placeholder="начни печатать имя модели…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto', display: 'grid', gap: 6 }}>
                {filtered.length === 0 && (
                  <div className="empty-state" style={{ padding: 20 }}>Ничего не найдено</div>
                )}
                {filtered.map((m) => (
                  <label
                    key={m.id}
                    className="model-card"
                    style={{
                      cursor: 'pointer',
                      borderColor: targetModelId === m.id ? 'var(--accent)' : undefined,
                      background: targetModelId === m.id ? 'var(--accent-soft)' : undefined,
                    }}
                  >
                    <input
                      type="radio"
                      name="target"
                      style={{ display: 'none' }}
                      checked={targetModelId === m.id}
                      onChange={() => setTargetModelId(m.id)}
                    />
                    <div className="model-avatar">{m.displayName.trim().charAt(0).toUpperCase()}</div>
                    <div className="info">
                      <div className="name">{m.displayName}</div>
                      <div className="meta">{m.accountCount} акк.</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <div className="field">
              <label>Название новой модели</label>
              <input
                className="admin-input"
                required
                autoFocus
                value={newModelName}
                onChange={(e) => setNewModelName(e.target.value)}
                placeholder="например: Виктория фри Кита"
              />
            </div>
          )}

          <div className="field">
            <label>Причина (для истории — не обязательно)</label>
            <input className="admin-input" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>

          <button className="btn-primary" disabled={busy}>{busy ? 'Переношу…' : 'Перенести'}</button>
          {err && <div className="error">{err}</div>}
        </form>
      </div>
    </>
  );
}
